import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { formatRent, formatDate, userTypeLabel } from '../../utils/format';

const LIFESTYLE_LABELS = {
  sleep_schedule: { early_bird: 'Early Bird', night_owl: 'Night Owl' },
  cleanliness: { very_clean: 'Very Clean', average: 'Average', relaxed: 'Relaxed' },
  noise_preference: { quiet: 'Quiet', social: 'Social' },
  guest_preference: { rarely: 'Rarely Has Guests', sometimes: 'Sometimes Has Guests', often: 'Often Has Guests' },
  study_habits: { at_home: 'Studies at Home', elsewhere: 'Studies Out' },
  pet_preference: { has_pets: 'Has Pets', no_pets: 'No Pets', okay_with_pets: 'Okay with Pets' },
};

function CompatibilityRing({ score }) {
  const color = score >= 80 ? '#C8A800' : score >= 60 ? '#16a34a' : score >= 40 ? '#d97706' : '#ef4444';
  return (
    <div className="flex flex-col items-center">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-black border-4"
        style={{ borderColor: color, color }}
      >
        {score}%
      </div>
      <span className="nav-link text-xs text-[#999999] mt-1">Match</span>
    </div>
  );
}

function RoommateCard({ user, onRequest, requesting }) {
  const tags = Object.entries(LIFESTYLE_LABELS)
    .filter(([key]) => user[key])
    .map(([key]) => LIFESTYLE_LABELS[key][user[key]])
    .filter(Boolean)
    .slice(0, 4);

  return (
    <div className="card p-5 transition-all duration-200 flex flex-col gap-4 hover:border-[#C8A800]/40 hover:shadow-md">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt={user.name} className="w-14 h-14 rounded-full object-cover border-2 border-[#C8A800]/30" />
          ) : (
            <div className="w-14 h-14 rounded-full flex items-center justify-center font-black text-xl" style={{ backgroundColor: 'rgba(200,168,0,0.12)', color: '#C8A800' }}>
              {user.name?.[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <h3 className="font-bold text-[#1B1B1B] text-lg">{user.name}</h3>
            <p className="nav-link text-sm text-[#777777]">{user.school}</p>
            {user.major && <p className="nav-link text-xs text-[#999999]">{user.major}</p>}
          </div>
        </div>
        <CompatibilityRing score={user.compatibility_score} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {tags.map(tag => (
          <span key={tag} className="badge-gray text-xs">{tag}</span>
        ))}
      </div>

      <div className="flex items-center justify-between text-sm">
        {user.budget_min && user.budget_max && (
          <span className="nav-link text-[#444444]">
            ${user.budget_min}–${user.budget_max}/mo
          </span>
        )}
        {user.move_in_date && (
          <span className="nav-link text-[#777777]">{formatDate(user.move_in_date)}</span>
        )}
      </div>

      {user.bio && (
        <p className="nav-link text-sm text-[#777777] line-clamp-2">{user.bio}</p>
      )}

      <button
        onClick={() => onRequest(user.id)}
        disabled={requesting === user.id}
        className="btn-primary w-full mt-auto"
      >
        {requesting === user.id ? (
          <span className="flex items-center justify-center gap-2">
            <span className="w-4 h-4 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
            Sending...
          </span>
        ) : 'Send Match Request'}
      </button>
    </div>
  );
}

export default function Feed() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.get('/roommates/feed').then(res => {
      setMatches(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const sendRequest = async (targetId) => {
    setRequesting(targetId);
    try {
      const res = await api.post(`/roommates/request/${targetId}`);
      setMatches(prev => prev.filter(m => m.id !== targetId));
      if (res.data.mutual) {
        setToast({ type: 'success', msg: "It's a match! You can now chat." });
      } else {
        setToast({ type: 'info', msg: 'Match request sent!' });
      }
      setTimeout(() => setToast(null), 3500);
    } catch (err) {
      setToast({ type: 'error', msg: err.response?.data?.error || 'Request failed' });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setRequesting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-xl text-sm font-medium transition-all ${
          toast.type === 'success' ? 'bg-green-600 text-white' :
          toast.type === 'error' ? 'bg-red-600 text-white' :
          'bg-white border border-[#C8A800] text-[#9A7F00] shadow-lg'
        }`}>
          {toast.msg}
        </div>
      )}

      {matches.length === 0 ? (
        <div className="text-center py-20">
          <svg className="w-10 h-10 text-[#DDDDDD] mb-4 mx-auto" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
          <p className="text-xl font-bold text-[#1B1B1B] mb-2">No new matches right now</p>
          <p className="nav-link text-[#777777]">Check back as more students join NestHawk!</p>
        </div>
      ) : (
        <>
          <p className="nav-link text-[#777777] mb-5">{matches.length} potential {matches.length === 1 ? 'match' : 'matches'} near you</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {matches.map(user => (
              <RoommateCard key={user.id} user={user} onRequest={sendRequest} requesting={requesting} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
